import { stripLegacyExclusiveSuffix } from "./trackCommerceCompatibility";

export const GUEST_CART_STORAGE_KEY = "vtk_guest_cart_v1";
export const GUEST_CART_SCHEMA_VERSION = 1;
const MAX_CART_ITEMS = 50;

function clean(value, max = 500) {
  return String(value ?? "").trim().slice(0, max);
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

export function cartProductType(item = {}) {
  const explicit = clean(item.productType || item.kind || item.type, 50).toLowerCase();
  if (["beat", "mix", "custom-beat", "recording", "merchandise", "service"].includes(explicit)) {
    return explicit;
  }
  if (item.isMix || item.mixType) return "mix";
  if (item.isCustomBeat) return "custom-beat";
  if (item.preBookingId || item.holdId || item.isRecordingSession) return "recording";
  if (item.variantId || item.sku || item.isMerchandise) return "merchandise";
  return "beat";
}

export function cartLicenseType(item = {}) {
  return item.exclusive === true || clean(item.license || item.licenseType, 30).toLowerCase() === "exclusive"
    ? "exclusive"
    : "lease";
}

function serviceIdentity(item = {}) {
  return clean(
    item.serviceRequestId || item.requestId || item.mixReqIds || item.mixReqId ||
      item.preBookingId || item.holdId || item.variantId || item.sku || item.id,
    1000,
  );
}

export function cartItemIdentity(item = {}) {
  const productType = cartProductType(item);
  if (productType === "beat") {
    const trackId = clean(item.trackId || item.id, 500);
    return trackId ? `beat:${trackId}:${cartLicenseType(item)}` : "";
  }
  const id = serviceIdentity(item);
  const subtype = productType === "mix" ? clean(item.mixType, 50).toLowerCase() : "";
  return id ? [productType, subtype, id].filter(Boolean).join(":") : "";
}

// A beat can only appear once in a checkout, even if a shopper tries to add a
// different license. This mirrors the server's duplicate-track validation.
export function cartSelectionGroup(item = {}) {
  const productType = cartProductType(item);
  if (productType === "beat") {
    const trackId = clean(item.trackId || item.id, 500);
    return trackId ? `beat:${trackId}` : "";
  }
  return cartItemIdentity(item);
}

export function sanitizeCartItem(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const productType = cartProductType(raw);
  const id = clean(raw.id || raw.trackId || serviceIdentity(raw), 1000);
  if (!id) return null;
  const license = cartLicenseType(raw);
  const item = {
    id,
    productType,
    title: clean(raw.title || raw.name || "Item", 240) || "Item",
    price: finiteNumber(raw.price ?? raw.displayPrice, 0),
    isMix: productType === "mix",
    license,
    exclusive: productType === "beat" && license === "exclusive",
    isBeatPack: Boolean(raw.isBeatPack),
  };

  if (raw.trackId || productType === "beat") item.trackId = clean(raw.trackId || id, 500);
  if (raw.basePrice != null) item.basePrice = finiteNumber(raw.basePrice, item.price);
  if (raw.discounted != null) item.discounted = Boolean(raw.discounted);
  if (raw.mixReqIds || raw.mixReqId) item.mixReqIds = clean(raw.mixReqIds || raw.mixReqId, 1000);
  if (raw.mixType) item.mixType = clean(raw.mixType, 50);
  if (raw.requestId) item.requestId = clean(raw.requestId, 500);
  if (raw.serviceRequestId) item.serviceRequestId = clean(raw.serviceRequestId, 500);
  if (raw.preBookingId) item.preBookingId = clean(raw.preBookingId, 500);
  if (raw.holdId) item.holdId = clean(raw.holdId, 500);
  if (raw.bpm != null && Number.isFinite(Number(raw.bpm))) item.bpm = Number(raw.bpm);
  if (raw.key != null) item.key = clean(raw.key, 40);
  if (raw.notes != null) item.notes = clean(raw.notes, 2000);
  if (raw.songTitle != null) item.songTitle = clean(raw.songTitle, 240);

  return cartItemIdentity(item) ? item : null;
}

export function sanitizeCartItems(list = []) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const groups = new Set();
  const safe = [];
  for (const raw of list.slice(0, MAX_CART_ITEMS)) {
    const item = sanitizeCartItem(raw);
    if (!item) continue;
    const identity = cartItemIdentity(item);
    const group = cartSelectionGroup(item);
    if (!identity || seen.has(identity) || (group && groups.has(group))) continue;
    seen.add(identity);
    if (group) groups.add(group);
    safe.push(item);
  }
  return safe;
}

export function serializeGuestCart(items = []) {
  const safeItems = sanitizeCartItems(items).map((item) => ({
    id: item.id,
    trackId: item.trackId,
    productType: item.productType,
    title: item.title,
    license: item.license,
    exclusive: item.exclusive,
    isMix: item.isMix,
    isBeatPack: item.isBeatPack,
    mixReqIds: item.mixReqIds,
    mixType: item.mixType,
    requestId: item.requestId,
    serviceRequestId: item.serviceRequestId,
    preBookingId: item.preBookingId,
    holdId: item.holdId,
    bpm: item.bpm,
    key: item.key,
    notes: item.notes,
    songTitle: item.songTitle,
    // Display snapshots keep refreshes usable. Checkout never trusts these;
    // the server re-prices every identity from Firestore/settings.
    displayPrice: item.price,
    displayBasePrice: item.basePrice,
  }));
  return JSON.stringify({ version: GUEST_CART_SCHEMA_VERSION, items: safeItems });
}

export function parseGuestCart(value) {
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    if (!parsed || parsed.version !== GUEST_CART_SCHEMA_VERSION || !Array.isArray(parsed.items)) return [];
    if (parsed.items.length > MAX_CART_ITEMS) return [];
    return sanitizeCartItems(parsed.items.map((item) => ({
      ...item,
      price: item.displayPrice,
      basePrice: item.displayBasePrice,
    })));
  } catch {
    return [];
  }
}

export function cartDisplayName(item = {}) {
  return stripLegacyExclusiveSuffix(item.title) || "Item";
}
